import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { listBronze, promoteBronze as apiPromote, removeBronze as apiRemove, bulkPromoteBronze as apiBulkPromote } from "./s3Api";

// Discovery / promotion state.
// Bronze (staged + promoted) is backed by /api/s3/bronze — a real MongoDB
// collection populated when the user stages an S3 object from the Schema
// Discovery drawer. Silver stays in-memory (still simulated) and Gold is
// deferred per the approved plan.
//
// Shape of a Bronze asset (from backend):
//   { id, connection_id, connection_name, bucket, key, format, size, rows,
//     row_groups, partition, columns:[{name,type,nullable,pii}],
//     suggested_table, primary_key, category, state:"staged"|"promoted",
//     staged_at, promoted_at, layer:"Bronze" }

const DiscoveryCtx = createContext({
  bronzeAssets: [],
  loading: false,
  refresh: async () => {},
  addStaged: () => {},
  removeStaged: async () => {},
  promote: async () => {},
  staged: [], promoted: [],
  silverStaged: [], silverPromoted: [],
  stageForSilver: () => {}, removeSilverStaged: () => {}, promoteToSilver: () => {},
});

function stagedFor(bronze) { return bronze.filter(a => a.state !== "promoted"); }
function promotedFor(bronze) { return bronze.filter(a => a.state === "promoted"); }

// Bronze payloads used to come from a simulated dataDiscovery module; the
// new Silver curation code expects fields (suggestedTable, rowsEst, sizeMb,
// aiCategoryConfidence, piiFlagged). Adapt the backend Bronze shape to the
// existing consumer without breaking either side.
function adaptForConsumers(a) {
  const cols = a.columns || [];
  const piiCount = cols.filter(c => c.pii).length;
  const sizeMb = a.size ? +(a.size / 1_048_576).toFixed(2) : 0;
  const rowsEst = a.rows ?? Math.max(1000, Math.round((a.size || 0) / 200));
  return {
    ...a,
    // legacy compat aliases used by DataManagement.jsx and SilverCurationDrawer
    connectionId: a.connection_id,
    connectionName: a.connection_name,
    suggestedTable: a.suggested_table,
    primaryKey: a.primary_key || "id",
    fileName: a.key,
    rowsEst,
    sizeMb,
    columns: cols,
    piiFlagged: piiCount,
    aiCategoryConfidence: 0.88,
    stagedAt: a.staged_at ? a.staged_at.slice(0, 16).replace("T", " ") : "just now",
    promotedAt: a.promoted_at ? a.promoted_at.slice(0, 16).replace("T", " ") : null,
    layer: "Bronze",
    // Iter 30: carry the origin tag through so the row renders a pill
    // ("Pre-existing" vs "1Rotary ingested"). Default to pre_existing
    // when backend emits an older doc without the field.
    origin: a.origin || "pre_existing",
  };
}

export function DiscoveryProvider({ children }) {
  const [bronzeAssets, setBronzeAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [silverStaged, setSilverStaged] = useState([]);
  const [silverPromoted, setSilverPromoted] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listBronze();
      const items = (res.assets || []).map(adaptForConsumers);
      setBronzeAssets(items);
    } catch {
      // silently keep whatever we had — backend may be temporarily down
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const staged = stagedFor(bronzeAssets);
  const promoted = promotedFor(bronzeAssets);

  function addStaged(asset) {
    // Called when the discovery drawer just persisted an asset — merge in fast.
    const list = Array.isArray(asset) ? asset : [asset];
    setBronzeAssets(prev => {
      const map = new Map(prev.map(a => [a.id, a]));
      for (const a of list) map.set(a.id, adaptForConsumers(a));
      return Array.from(map.values());
    });
  }

  async function removeStaged(id) {
    setBronzeAssets(prev => prev.filter(a => a.id !== id));
    try { await apiRemove(id); } catch { /* ignore */ }
  }

  async function promote(id) {
    setBronzeAssets(prev => prev.map(a => a.id === id ? { ...a, state: "promoted", promotedAt: new Date().toISOString().slice(0, 16).replace("T", " ") } : a));
    try { await apiPromote(id); } catch { /* ignore */ }
  }

  async function bulkPromote({ ids, connection_id, partition }) {
    // 1) resolve target ids up-front so the optimistic update matches the
    //    backend's target set exactly.
    const targetIds = ids && ids.length
      ? ids
      : bronzeAssets
          .filter(a =>
            (!connection_id || a.connection_id === connection_id) &&
            (!partition || (a.partition || "") === partition) &&
            a.state !== "promoted"
          )
          .map(a => a.id);
    // 2) optimistic update
    if (targetIds.length) {
      setBronzeAssets(prev => prev.map(a =>
        targetIds.includes(a.id)
          ? { ...a, state: "promoted", promotedAt: new Date().toISOString().slice(0, 16).replace("T", " ") }
          : a));
    }
    // 3) fire the network call using explicit ids to avoid partition-string
    //    normalization drift between client and server.
    try {
      const res = await apiBulkPromote({ ids: targetIds });
      // 4) refresh to pick up server-normalized timestamps
      const listed = await listBronze();
      setBronzeAssets((listed.assets || []).map(adaptForConsumers));
      return res;
    } catch (e) {
      // 5) on failure, roll back the optimistic update via a full refresh
      const listed = await listBronze();
      setBronzeAssets((listed.assets || []).map(adaptForConsumers));
      throw e;
    }
  }

  function stageForSilver(silverCandidate) {
    setSilverStaged(prev => {
      const existing = prev.find(c => c.id === silverCandidate.id);
      if (existing) {
        return prev.map(c => c.id === silverCandidate.id
          ? { ...silverCandidate, stagedAt: new Date().toISOString().slice(0, 16).replace("T", " ") }
          : c);
      }
      return [...prev, { ...silverCandidate, stagedAt: new Date().toISOString().slice(0, 16).replace("T", " ") }];
    });
  }
  function removeSilverStaged(id) {
    setSilverStaged(prev => prev.filter(c => c.id !== id));
  }
  function promoteToSilver(id) {
    const cand = silverStaged.find(c => c.id === id);
    if (!cand) return;
    setSilverPromoted(prev => [...prev, { ...cand, promotedAt: new Date().toISOString().slice(0, 16).replace("T", " ") }]);
    setSilverStaged(prev => prev.filter(c => c.id !== id));
  }

  return (
    <DiscoveryCtx.Provider value={{
      bronzeAssets, loading, refresh,
      staged, promoted,
      addStaged, removeStaged, promote, bulkPromote,
      silverStaged, silverPromoted,
      stageForSilver, removeSilverStaged, promoteToSilver,
    }}>
      {children}
    </DiscoveryCtx.Provider>
  );
}

export const useDiscovery = () => useContext(DiscoveryCtx);
